import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getClientDefaultLandingPage,
  updateClientDefaultLandingPage,
} from "../../services/accountService";
import { landingPageOptions, pages } from "../../constants";

/* type clientLandingPagePreference = {
  data: {
    UserId: string;
    HomePage: string;
    EnterBy: string;
    EnterDate: Date;
    ModifyBy: string;
    ModifyDate: Date | null;
  } |  "Not Found!";
Assumed "Not Found!" is a undefined value


interface PostAccountDefaultLandingPage {
  HomePage: string;
  isModified: boolean;
}

interface LandingPageSelectorEventsRef {
  resetLandingPageOptions: () => void;
  submitLandingPageOptions: () => void;
}

interface LandingPageProps {
  isLandingOptionsModified?: (value: boolean) => void;
}
*/

export const getLandingPageUrl = (landingPageValue) => {
  if (!landingPageValue || landingPageValue === "Not Found!") {
    return pages.mainPage.url;
  }

  return (
    Object.values(landingPageOptions).find(
      (option) => option.value === landingPageValue
    )?.url || pages.mainPage.url
  );
};

const LandingPageSelector = forwardRef(({ isLandingOptionsModified }, ref) => {
  const [currentLandingPage, setCurrentLandingPage] = useState(
    landingPageOptions.dashboard.value
  );

  const queryClient = useQueryClient();

  const { data: responseLandingPageData, isLoading } = useQuery({
    queryKey: ["default-landing-page"],
    queryFn: getClientDefaultLandingPage,
  });

  const { mutate } = useMutation({
    mutationFn: updateClientDefaultLandingPage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["default-landing-page"],
      });

      if (isLandingOptionsModified) {
        isLandingOptionsModified(false);
      }
    },
  });

  const validatedNewDefaultLandingPage =
    responseLandingPageData?.data?.homePage &&
    currentLandingPage !== responseLandingPageData?.data?.homePage;

  const handleReset = () => {
    if (isLandingOptionsModified) {
      isLandingOptionsModified(false);
    }

    if (!responseLandingPageData?.data?.homePage) {
      setCurrentLandingPage(landingPageOptions[0].value);
      return;
    }

    setCurrentLandingPage(responseLandingPageData.data.homePage);
  };

  const handleSubmit = () => {
    // Check if there are any changes
    if (!validatedNewDefaultLandingPage) {
      return;
    }

    // New settings

    if (!responseLandingPageData?.data?.homePage) {
      mutate({
        HomePage: currentLandingPage,
        isModified: false,
      });

      return;
    }

    // Update settings
    mutate({
      HomePage: currentLandingPage,
      isModified: true,
    });
  };

  useEffect(() => {
    if (!responseLandingPageData?.data?.homePage) return;

    setCurrentLandingPage(responseLandingPageData.data.homePage);
  }, [responseLandingPageData?.data?.homePage]);

  useImperativeHandle(ref, () => ({
    resetLandingPageOptions: handleReset,
    saveLandingPageOptions: handleSubmit,
  }));

  return (
    <div className="form-group d-flex flex-column flex-xl-row">
      <label
        className="text-nowrap mr-xl-2 mb-0"
        htmlFor="default-landing-setting"
      >
        Default landing page:
      </label>
      <select
        className="form-control form-control-sm"
        id="default-landing-setting"
        value={currentLandingPage}
        disabled={isLoading}
        onChange={(event) => {
          if (isLandingOptionsModified) {
            const isNewChange =
              responseLandingPageData?.data.homePage !== event.target.value;

            isLandingOptionsModified(isNewChange);
          }
          setCurrentLandingPage(event.target.value);
        }}
      >
        {Object.values(landingPageOptions).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
});

LandingPageSelector.displayName = "LandingPageSelector";

export default LandingPageSelector;
